import { Title } from '@components';
import { useAppSelector, useIntersectionObserver, useScrollPosition } from '@hooks';
import { Box, Stack, styled } from '@mui/material';
import { isInTransition } from '@store/product/product.slice';
import { ReactNode, useLayoutEffect, useRef } from 'react';

// STYLES
//--------
const CollapsibleSectionBox = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 0 64px 0;
  }
`;

// TYPES
//-------
export type CollapsibleSectionProps = {
  sectionId?: string;
  title?: string;
  children?: ReactNode;
  onSectionEnter?: (sectionId: string) => any;
};

export const ProductSection = ({
  sectionId = '',
  title,
  children,
  onSectionEnter = (sectionId: string) => null,
}: CollapsibleSectionProps) => {
  const offsetTop = 80;
  const ref = useRef<HTMLDivElement | null>(null);
  const position = useScrollPosition();
  const inTransition = useAppSelector(isInTransition);
  const entry = useIntersectionObserver(ref, { threshold: 0 });
  const isVisible = !!entry?.isIntersecting;
  useLayoutEffect(() => {
    if (inTransition || !isVisible) {
      return;
    }
    const top = ref.current?.getBoundingClientRect().top;
    if (top && top - offsetTop <= 0) {
      onSectionEnter(sectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, position, inTransition]);
  return (
    <CollapsibleSectionBox className='product-section'>
      <Box component='a' id={sectionId} ref={ref} />
      <Stack spacing={2}>
        {title && <Title variant='h3'>{title}</Title>}
        {children}
      </Stack>
    </CollapsibleSectionBox>
  );
};
